import { useCallback, useEffect, type FC } from "react";

import { AutocompleteTypeEnum, useFetchEntitiesAutocomplete } from "@ds/hooks/use-fetch-entities";
import { useIsFirstTimeTrue } from "@ds/hooks/use-is-first-time-true";
import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { AutoComplete, type SingleAutoCompleteProps } from "@ds/components/forms/autocomplete";
import { SpinnerIcon } from "@ds/components/loaders/loaders";

import { selectCampaignById, selectCampaignsIsLoading } from "../redux/selectors";
import { campaignsActions } from "../redux/slice";
import { isCampaign } from "../utils/model";

export const CampaignsAutoComplete: FC<Omit<SingleAutoCompleteProps<Campaign>, "isLoading">> = ({
  inputRef,
  value,
  filterSuggestionsPredicate,
  dropdownMode,
  autoFocus,
  onBlur,
  onChange,
}) => {
  const [
    suggestions,
    onCompleteHandler,
    {
      state: { status: remoteStatus },
    },
  ] = useFetchEntitiesAutocomplete<Campaign>(AutocompleteTypeEnum.Campaigns, {
    filterPredicate: filterSuggestionsPredicate,
  });

  return (
    <AutoComplete
      field="name"
      placeholder="Enter campaign name"
      inputRef={inputRef}
      value={value}
      suggestions={suggestions}
      dropdownMode={dropdownMode}
      autoFocus={autoFocus}
      isLoading={remoteStatus === "loading"}
      onBlur={onBlur}
      onChange={onChange}
      onComplete={onCompleteHandler}
    />
  );
};

export const CampaignsAutocompleteRaw: FC<
  Omit<SingleAutoCompleteProps<Campaign>, "value" | "onChange" | "isLoading"> & {
    value?: number | null;
    onChange: (id: number | null, campaign: Campaign | null) => void;
  }
> = ({ value, onChange, ...restProps }) => {
  const isLoading = useRootSelector(selectCampaignsIsLoading);
  const currentValue = useRootSelector(state =>
    isCampaign(value) ? value : selectCampaignById(state, Number(value) || 0),
  );

  const [isFirstLoading] = useIsFirstTimeTrue(isLoading);

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (!isCampaign(value) && Number(value)) {
      dispatch(campaignsActions.fetchCampaign(Number(value), { shouldDisableSagaThrottling: true }));
    }
  }, [dispatch, value]);

  const onChangeHandler = useCallback(
    (campaign: Campaign) => {
      onChange(campaign?.id ?? null, campaign ?? null);
    },
    [onChange],
  );

  return isFirstLoading ? (
    <SpinnerIcon />
  ) : (
    <CampaignsAutoComplete value={currentValue} onChange={onChangeHandler} {...restProps} />
  );
};
